/**
 * The Lyrixa AI masthead that appears only on paper.
 *
 * WHY A COMPONENT AND NOT A STRING. The other two print surfaces — the lyric sheet and
 * the licence certificate — build a whole HTML document and hand it to a new window, so
 * they use lib/printBrand's SVG string. The agreement is different: its Print button
 * calls `window.print()` on the page the user is already looking at, because the
 * agreement IS the legal text on screen and rebuilding it into a second document would
 * mean two copies of legal copy that could drift apart. So the branding has to live in
 * the real DOM, which means React — and therefore ui/Logo, the same component the sidebar
 * and the login screen draw, rather than a fourth hand-built copy of the lockup.
 *
 * THE MARK IS MONOCHROME HERE AND THE AMBER COMES FROM `.lyx-print-badge`. The square is a
 * CSS background rather than an SVG fill because a printer drops backgrounds by default and
 * the one rule that overrides it — `print-color-adjust: exact` — is already scoped to that
 * class in the print block. Moving the amber into the SVG would mean re-establishing that
 * guarantee somewhere new, for no gain.
 *
 * Invisible on screen — `display: none` until `@media print`, see global.css. It adds no
 * pixels to the page, which is the brief's "do not disrupt normal screen UI" taken
 * literally: there is nothing to disrupt because there is nothing there.
 *
 * `aria-hidden` because a screen-reader user gets nothing from a masthead that only
 * exists on paper; the page already has its own <h1>.
 */
import { BRAND_INK, BRAND_NAME } from '../../lib/brandMark';
import Logo from './Logo';

export interface PrintHeaderProps {
  /**
   * The document kind, printed small and uppercase opposite the mark — "Commercial
   * License Agreement". Matches the `kind` slot on the built documents so all three
   * print surfaces read the same way.
   */
  kind: string;
  /** Optional line under the rule, e.g. the version or last-updated label. */
  note?: string;
}

export default function PrintHeader({ kind, note }: PrintHeaderProps) {
  return (
    <header className="lyx-print-header" aria-hidden="true">
      <div className="lyx-print-header-row">
        <span className="lyx-print-brand">
          {/*
            The badge is the stylesheet's (amber background, colour-adjust: exact) and the
            envelope inside it is the shared mark, drawn in the on-accent ink. `decorative`
            because the whole masthead is aria-hidden and the wordmark beside it is the
            visible name.

            The wordmark stays a `.lyx-print-wordmark` span rather than becoming Logo's
            `full` variant: its size is 13pt, set in the print block, and Logo sizes its
            words in px from the mark — an inline px font-size would outrank the point size
            and put screen typography on paper.
          */}
          <span className="lyx-print-badge">
            <Logo variant="mark" tone="mono" size={15} color={BRAND_INK} decorative />
          </span>
          <span className="lyx-print-wordmark">{BRAND_NAME}</span>
        </span>
        <span className="lyx-print-kind">{kind}</span>
      </div>
      {note && <p className="lyx-print-note">{note}</p>}
    </header>
  );
}
